import { useState, useEffect, useCallback } from "react";
import {
  X,
  BarChart3,
  Database,
  Table,
  Clock,
  Settings,
  Fan,
} from "lucide-react";
import axios from "../../utils/axiosInstance";
import DataTable from "../../../src/components/DataTable/DataTable.jsx";
import RuntimeReport from "../../../src/components/RuntimeReport/RuntimeReport.js";
import { MainPlungerPage } from "../../../src/components/plungerPage/mainPlungerPage.jsx";
import ChartManager from "../../../src/components/ChartManager.jsx";
import TimeRangeSelector from "../../../src/components/TimeRangeSelector.jsx";
import { User } from "../util/types";
import FanClutch from "../../components/FanClutch/FanClutch.jsx";
import Header from "../../components/fieldData/Header.jsx";
import CompressorData from "../../components/fieldData/CompressorData.jsx";
import Telemetry from "../../components/fieldData/Telemetry.jsx";
import EngineData from "../../components/fieldData/EngineData.jsx";
import DeviceLocation from "../../components/DeviceLocation.jsx";
import { CircularProgress } from "@mui/material";
import type { Modem } from "../types/types";

interface EngineData {
  id?: string;
  deviceId?: string;
  timestamp?: string;
  COMP_HOURMETER?: string;
  COMP_OIL_PRES?: number;
  CTRL_TYPE?: string;
  DISCHARGE_2_TEMP?: number;
  DISCHARGE_PRES?: number;
  DISCHARGE_TEMP_FINAL?: number;
  ENGINE_COOLANT_TEMP?: number;
  ENGINE_HOURMETER?: number;
  ENGINE_MANIFOLD_PRES?: number;
  ENGINE_OIL_PRES?: number;
  ENGINE_OIL_TEMP?: number;
  ENGINE_SPEED?: number;
  FIELD_PRES?: number;
  FLOW_METER?: number;
  FLOW_RATE_SP?: number;
  INTERSTAGE_PRES?: number;
  LAT?: number;
  LON?: number;
  SHUTDOWN_ENUM?: number;
  SUCTION_PRES?: number;
  SYSTEM_VOTLS?: number;
  TAILSHAFT_TEMP?: number;
  AI7?: number;
  AI8?: number;
  AI9?: number;
  AI10?: number;
  AI11?: number;
  AI12?: number;
  CM1?: number;
  CM2?: number;
  CM3?: number;
  CM4?: number;
  FMI1?: number;
  FMI2?: number;
  FMI3?: number;
  FMI4?: number;
  SPN1?: number;
  SPN2?: number;
  SPN3?: number;
  SPN4?: number;
  STATE?: number;
  R40797?: number;
  R40798?: number;
  R40799?: number;
  R40800?: number;
  R42487?: number;
  R42488?: number;
  R42489?: number;
  R42490?: number;
  error?: string;
  [key: string]: any;
}

export interface ChartData {
  COMP_OIL_PRES: number[];
  DISCHARGE_PRES: number[];
  DISCHARGE_TEMP_FINAL: number[];
  ENGINE_COOLANT_TEMP: number[];
  ENGINE_HOURMETER: number[];
  ENGINE_SPEED: number[];
  FIELD_PRES: number[];
  FLOW_METER: number[];
  INTERSTAGE_PRES: number[];
  SHUTDOWN_ENUM: number[];
  STATE: number[];
  SUCTION_PRES: number[];
  SYSTEM_VOTLS: number[];
  time: number[];
}

interface EnhancedModem extends Modem {
  engineData?: EngineData;
  engineDataLoading?: boolean;
}

interface ModemDetailProps {
  modem: EnhancedModem;
  onClose: () => void;
  isFullView?: boolean;
}

type DetailTab =
  | "field-data"
  | "charts"
  | "data-table"
  | "runtime-report"
  // | "control"
  | "plunger-page"
  | "fan-clutch";

export function ModemDetail({
  modem,
  onClose,
  isFullView = false,
}: ModemDetailProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>("field-data");

  const [timeRange, setTimeRange] = useState<{
    type: "preset" | "custom";
    hours?: number;
    start?: number;
    end?: number;
  }>({ type: "preset", hours: 168 });

  const [chartData, setChartData] = useState<any | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  // Needed to determine which registers to request from the chart API
  const [ctrlType, setCtrlType] = useState<string>("");

  const [engineData, setEngineData] = useState<any>(null);
  const [loadingEngineData, setLoadingEngineData] = useState(false);

  // Needed to pre-populate selectedRegisters from user's saved preferences
  const [user, setUser] = useState<User | null>(null);
  const [loadedFromServer, setLoadedFromServer] = useState(false);

  const allRegisters = [
    "COMP_OIL_PRES",
    "DISCHARGE_PRES",
    "DISCHARGE_TEMP_FINAL",
    "ENGINE_COOLANT_TEMP",
    "ENGINE_HOURMETER",
    "ENGINE_SPEED",
    "FIELD_PRES",
    "FLOW_METER",
    "INTERSTAGE_PRES",
    "SUCTION_PRES",
    "SYSTEM_VOTLS",
    "TAILSHAFT_TEMP",
    "STATE",
    "FLOW_RATE_SP",
    "ENGINE_OIL_TEMP",
    "ENGINE_OIL_PRES",
    "ENGINE_MANIFOLD_PRES",
    "DISCHARGE_2_TEMP",
    "COMP_HOURMETER",
    "AI10",
    "AI11",
    "AI12",
  ];

  const [selectedRegisters, setSelectedRegisters] = useState<string[]>([
    ...allRegisters,
  ]);

  // Needed for Control and Plunger Page tabs
  const [device, setDevice] = useState<{
    isActive?: boolean;
    id?: string;
    [key: string]: any;
  } | null>(null);

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) setUser(JSON.parse(userString));
  }, []);

  const fetchEngineData = useCallback(async (deviceId: string) => {
    if (!deviceId) return;
    try {
      setLoadingEngineData(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/engine-data/${deviceId}`,
      );
      setEngineData(response.data);
      setCtrlType(response.data.type || "");
    } catch (err) {
      console.error(err);
      setEngineData(null);
    } finally {
      setLoadingEngineData(false);
    }
  }, []);

  useEffect(() => {
    if (modem?.id) fetchEngineData(modem?.id);
  }, [modem?.id, fetchEngineData]);

  // Load user's saved chart register preferences from server
  useEffect(() => {
    const localUserString = localStorage.getItem("user");
    if (!localUserString || !ctrlType || loadedFromServer) return;

    const localUser = JSON.parse(localUserString) as { id: string };

    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/users/${localUser.id}`,
        );
        const serverUser = res.data;
        setUser(serverUser);
        setLoadedFromServer(true);

        let reg: string[] = [];
        if (ctrlType === "C5E" || ctrlType === "C5") {
          const chartFields = serverUser.ChartFieldsC5;
          reg =
            typeof chartFields === "string"
              ? JSON.parse(chartFields)
              : Array.isArray(chartFields)
                ? chartFields
                : [];
        } else if (ctrlType === "WellPro II") {
          const chartFields = serverUser.ChartFieldsWELLPRO;
          reg =
            typeof chartFields === "string"
              ? JSON.parse(chartFields)
              : Array.isArray(chartFields)
                ? chartFields
                : [];
        }

        if (reg.length > 0) setSelectedRegisters(reg);
      } catch (err) {
        console.error(err);
      }
    };

    fetchUser();
  }, [ctrlType, loadedFromServer]);

  useEffect(() => {
    setLoadedFromServer(false);
  }, [modem?.id, ctrlType]);

  const fetchChartData = useCallback(
    async (deviceId: string | number) => {
      if (!deviceId || !timeRange) return;
      if (!["C5E", "C5", "WellPro II"].includes(ctrlType)) {
        console.warn("Unsupported controller type:", ctrlType);
        return;
      }
      try {
        setChartLoading(true);

        let start: number;
        let end: number;

        if (timeRange.type === "custom" && timeRange.start && timeRange.end) {
          start = timeRange.start;
          end = timeRange.end;
        } else {
          end = Math.floor(Date.now() / 1000);
          start = end - (timeRange.hours ?? 4) * 3600;
        }

        const url = `${process.env.REACT_APP_API_GATEAWAY}/iot/chart-api/${deviceId}/${start}/${end}`;
        let body: string[] = [];

        if (ctrlType === "C5E" || ctrlType === "C5") {
          body = [
            "COMP_HOURMETER",
            "DISCHARGE_2_TEMP",
            "DISCHARGE_TEMP_FINAL",
            "ENGINE_COOLANT_TEMP",
            "ENGINE_HOURMETER",
            "ENGINE_OIL_PRES",
            "ENGINE_MANIFOLD_PRES",
            "ENGINE_OIL_TEMP",
            "FLOW_RATE_SP",
            "TAILSHAFT_TEMP",
            "COMP_OIL_PRES",
            "DISCHARGE_PRES",
            "ENGINE_SPEED",
            "FIELD_PRES",
            "FLOW_METER",
            "INTERSTAGE_PRES",
            "SUCTION_PRES",
            "SYSTEM_VOTLS",
            "SPN1",
            "SPN2",
            "SPN3",
            "SPN4",
            "FMI1",
            "FMI2",
            "FMI3",
            "FMI4",
            "CM1",
            "CM2",
            "CM3",
            "CM4",
            "LAT",
            "LON",
            "AI10",
            "AI11",
            "AI12",
            "AI7",
            "AI8",
            "AI9",
          ];
        } else if (ctrlType === "WellPro II") {
          body = [
            "AI10",
            "AI11",
            "AI12",
            "COMP_OIL_PRES",
            "DISCHARGE_2_TEMP",
            "DISCHARGE_PRES",
            "DISCHARGE_TEMP_FINAL",
            "ENGINE_COOLANT_TEMP",
            "ENGINE_HOURMETER",
            "ENGINE_OIL_PRES",
            "ENGINE_MANIFOLD_PRES",
            "ENGINE_OIL_TEMP",
            "ENGINE_SPEED",
            "FIELD_PRES",
            "FLOW_METER",
            "FLOW_RATE_SP",
            "INTERSTAGE_PRES",
            "STATE",
            "SUCTION_PRES",
            "SYSTEM_VOTLS",
            "TAILSHAFT_TEMP",
            "SHUTDOWN_ENUM",
          ];
        }
        if (body.length === 0) {
          console.warn("Chart API request skipped: no registers configured.");
          return;
        }
        const response = await axios.post(url, body);
        const apiData = response.data?.data?.response;
        if (!apiData || !apiData.time) throw new Error("Invalid API response");
        setChartData(apiData);
      } catch (e) {
        console.error("❌ Failed to fetch chart data:", e);
        setChartData(null);
      } finally {
        setChartLoading(false);
      }
    },
    [timeRange, ctrlType],
  );

  useEffect(() => {
    if (!modem?.id || !ctrlType) return;
    fetchChartData(modem.id);
  }, [modem?.id, ctrlType, timeRange, fetchChartData]);

  // Needed for Control and Plunger Page tabs
  useEffect(() => {
    const fetchDevice = async () => {
      if (!modem?.id) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices/details/${modem.id}`,
        );
        setDevice(response.data);
      } catch (err) {
        console.error("❌ Error fetching device:", err);
      }
    };
    fetchDevice();
  }, [modem?.id]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "online":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "alarm":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "offline":
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
      case "invalid-data":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    }
  };

  const renderFieldDataContent = () => {
    return (
      <div>
        {loadingEngineData ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress size={60} />
          </div>
        ) : (
          <>
        <Header
          onRefresh={() => fetchEngineData(modem.id)}
          onPoll={() => fetchEngineData(modem.id)}
          loading={loadingEngineData}
          deviceName={modem.name}
          deviceId={modem.id}
        />

        {!device?.isActive && (
          <div className="mb-2 font-bold text-center bg-[#0f1530ff] text-yellow-600 p-3 rounded">
            Unit in Standby
          </div>
        )}

        <div className="flex flex-col xl:flex-row gap-2 mt-1">
          {/* LEFT SECTION */}
          <div className="flex-1 flex flex-col gap-0.5">
            {/* ENGINE + COMPRESSOR */}
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1">
                <EngineData data={engineData} />
              </div>
              <div className="flex-1">
                <CompressorData data={engineData} />
              </div>
            </div>

            {/* TELEMETRY */}
            <div className="mt-1">
              <Telemetry data={engineData} />
            </div>
          </div>

          {/* RIGHT SECTION — MAP */}
          <div className="bg-slate-800 p-3 flex-1 flex flex-col mt-2 xl:mt-0">
            <h3 className="text-sm font-semibold text-white mb-2">Location</h3>
            <div className="flex-1 overflow-hidden h-64 xl:h-auto">
              <DeviceLocation
                latitude={engineData?.response?.LAT || 0}
                longitude={engineData?.response?.LON || 0}
              />
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    );
  };

  const renderRuntimeReportContent = () => (
    <RuntimeReport device={{ id: modem.id, name: modem.name }} />
  );

  // Uses JSX ChartManager and TimeRangeSelector directly
  const renderChartsContent = () => {
    if (!chartData) return null;
    return (
      <div className="p-3">
        <TimeRangeSelector
          deviceId={modem.id}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          onRefresh={() => fetchChartData(modem.id)}
          loading={chartLoading}
          selectedRegisters={selectedRegisters}
          setSelectedRegisters={setSelectedRegisters}
        />
        <ChartManager
          timeRange={timeRange}
          chartData={chartData}
          selectedRegisters={selectedRegisters}
        />
      </div>
    );
  };

  // DataTable is self-sufficient — no tableData/fetchTableData needed here
  const renderDataTableContent = () => (
    <DataTable device={{ id: modem.id, name: modem.name }} />
  );

  // const renderControlContent = () => <ControlPage selectedDevice={device} />;

  const renderPlungerPageContent = () => (
    <MainPlungerPage selectedDevice={device} />
  );

  const renderFanClutchContent = () => <FanClutch selectedDevice={device} />;

  // --- isFullView render ---
  if (isFullView) {
    return (
      <div className="w-full">
        <div className="bg-slate-900 border border-slate-800 mb-3 p-2 sm:p-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end mb-3 gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-sm transition-colors"
            >
              Back to Overview
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
            <div className="bg-slate-800 p-2.5">
              <div className="text-sm text-slate-400 mb-1">Status</div>
              <div
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium border capitalize ${getStatusBadgeClass(modem.operational_status)}`}
              >
                {modem.operational_status === "offline"
                  ? "Missed Com"
                  : modem.operational_status}
              </div>
            </div>
            <div className="bg-slate-800 p-2.5">
              <div className="text-sm text-slate-400 mb-1">Location</div>
              <div className="text-white font-medium">
                {modem.latitude.toFixed(4)}, {modem.longitude.toFixed(4)}
              </div>
            </div>
            <div className="p-2.5 bg-slate-800">
              <div className="text-sm text-slate-400 mb-1">Last Update</div>
              <div className="text-white font-medium">
                {new Date(modem.last_update).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="flex flex-row gap-2 mb-3 overflow-x-auto whitespace-nowrap">
            {(
              [
                "field-data",
                "charts",
                "data-table",
                "runtime-report",
                "plunger-page",
                "fan-clutch",
              ] as DetailTab[]
            ).map((tab) => {
              const icons: Record<DetailTab, React.ReactNode> = {
                "field-data": <Database className="w-4 h-4" />,
                charts: <BarChart3 className="w-4 h-4" />,
                "data-table": <Table className="w-4 h-4" />,
                "runtime-report": <Clock className="w-4 h-4" />,
                // "control":        <Podcast className="w-4 h-4" />,
                "plunger-page": <Settings className="w-4 h-4" />,
                "fan-clutch": <Fan className="w-4 h-4" />,
              };
              const labels: Record<DetailTab, string> = {
                "field-data": "Field Data",
                charts: "Charts",
                "data-table": "Data Table",
                "runtime-report": "Downtime Report",
                // "control":        "Control",
                "plunger-page": "Plunger Page",
                "fan-clutch": "Fan Clutch",
              };
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? "bg-blue-600 text-white"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                >
                  {icons[tab]}
                  {labels[tab]}
                </button>
              );
            })}
          </div>

          <div className="w-full overflow-x-auto">
            {activeTab === "field-data" && renderFieldDataContent()}
            {activeTab === "charts" && renderChartsContent()}
            {activeTab === "data-table" && renderDataTableContent()}
            {activeTab === "runtime-report" && renderRuntimeReportContent()}
            {/* {activeTab === "control"        && renderControlContent()} */}
            {activeTab === "plunger-page" && renderPlungerPageContent()}
            {activeTab === "fan-clutch" && renderFanClutchContent()}
          </div>
        </div>
      </div>
    );
  }

  // --- Modal render ---
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-4 py-3 z-10">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-xl font-bold text-white">{modem.name}</h2>
              <p className="text-sm text-slate-400 mt-1">{modem.unit_name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="flex flex-row gap-2 mb-3 overflow-x-auto no-scrollbar">
            {(["field-data", "charts", "data-table"] as DetailTab[]).map(
              (tab) => {
                const icons: Record<string, React.ReactNode> = {
                  "field-data": <Database className="w-4 h-4" />,
                  charts: <BarChart3 className="w-4 h-4" />,
                  "data-table": <Table className="w-4 h-4" />,
                };
                const labels: Record<string, string> = {
                  "field-data": "Field Data",
                  charts: "Charts",
                  "data-table": "Data Table",
                };
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? "bg-blue-600 text-white"
                        : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    }`}
                  >
                    {icons[tab]}
                    {labels[tab]}
                  </button>
                );
              },
            )}
          </div>
        </div>

        <div className="p-3">
          {activeTab === "field-data" && renderFieldDataContent()}
          {activeTab === "charts" && renderChartsContent()}
          {activeTab === "data-table" && renderDataTableContent()}
          {activeTab === "runtime-report" && renderRuntimeReportContent()}
        </div>
      </div>
    </div>
  );
}
